/**
 * KioskGeraeteVerwaltung — Uebersicht der registrierten Stempel-Terminals (#1809-Fix).
 *
 * Ein Terminal wird hier angelegt und liefert einen 8-stelligen Kopplungscode,
 * der am Tablet unter /geraet-einrichten eingegeben wird. Die Registrierung
 * regelt den ZUGANG (#1816): ein gekoppeltes Geraet ist ohne PIN scanbereit und
 * gilt als Kiosk (hart hell, kein Dark-Mode-Schalter, Button statt Slider).
 *
 * DEAKTIVIERT != WIDERRUFEN. Deaktiviert ist eine voruebergehende
 * Ausserbetriebnahme (Umbau, Wartung, Feierabend) bei erhaltener Registrierung;
 * entsperrt wird am Tablet mit der Terminal-PIN — oder von hier aus, weil sonst
 * ein gesperrtes Tablet in der Halle steht und niemand die PIN kennt. Widerruf
 * dagegen entwertet den Device-Token: das Tablet faellt beim naechsten
 * Seitenaufruf auf die persoenliche Ansicht zurueck, die laufende Anmeldung
 * bleibt bestehen, gestempelt werden kann durchgehend.
 *
 * @component
 * @endpoints GET/POST /api/kiosk-geraete, POST /api/kiosk-geraete/:id/{code-neu,widerrufen,entsperren}
 * @since v2.867.0
 * @updated v2.871.0 — Sperrzustand sichtbar + Fernentsperrung (#1816)
 */
import { useState, useEffect, useCallback } from 'react';
import { MonitorSmartphone, Plus, X } from 'lucide-react';
import KioskGeraetDetail from './KioskGeraetDetail';

const API = import.meta.env.VITE_API_URL || '';

const STATUS_TEXT = {
  aktiv: 'Gekoppelt',
  gesperrt: 'Deaktiviert',
  wartet_auf_kopplung: 'Wartet auf Kopplung',
  widerrufen: 'Widerrufen',
};

const STATUS_STYLE = {
  aktiv: { background: '#DCFCE7', color: '#166534' },
  gesperrt: { background: '#FEE2E2', color: '#991B1B' },
  wartet_auf_kopplung: { background: '#FEF3C7', color: '#92400E' },
  widerrufen: { background: 'var(--bg-inset, #F8F9FC)', color: 'var(--text-tertiary, #6B7280)' },
};

function datum(wert) {
  if (!wert) return '–';
  return new Date(wert).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

export default function KioskGeraeteVerwaltung({ t, showToast }) {
  const [geraete, setGeraete] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOffen, setFormOffen] = useState(false);
  const [name, setName] = useState('');
  const [standort, setStandort] = useState('');
  const [saving, setSaving] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const laden = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/kiosk-geraete`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setGeraete(data.geraete || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const anlegen = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/kiosk-geraete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), standort: standort.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.error || 'Anlegen fehlgeschlagen'); return; }
      setName(''); setStandort(''); setFormOffen(false);
      await laden();
    } finally {
      setSaving(false);
    }
  };

  const codeNeu = async (id) => {
    const res = await fetch(`${API}/api/kiosk-geraete/${id}/code-neu`, { method: 'POST', credentials: 'include' });
    if (res.ok) { await laden(); showToast('Neuer Kopplungscode erstellt'); }
  };

  const widerrufen = async (id) => {
    const res = await fetch(`${API}/api/kiosk-geraete/${id}/widerrufen`, { method: 'POST', credentials: 'include' });
    if (res.ok) { await laden(); showToast('Registrierung widerrufen'); }
  };

  // #1816 — ohne diesen Weg stünde ein deaktiviertes Tablet in der Halle und
  // niemand vor Ort kennt die Terminal-PIN.
  const entsperren = async (id) => {
    const res = await fetch(`${API}/api/kiosk-geraete/${id}/entsperren`, { method: 'POST', credentials: 'include' });
    if (res.ok) { await laden(); showToast('Terminal wieder in Betrieb'); }
  };

  return (
    <div className="bg-surface-elevated rounded-card border border-line">
      <div className="px-5 py-3 border-b border-line flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MonitorSmartphone className="w-4 h-4" style={{ color: '#338edc' }} />
          <h2 className="text-sm font-semibold text-content">
            {t('stempel.terminals.title', 'Terminal')}
          </h2>
        </div>
        <button
          onClick={() => setFormOffen(v => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-white transition-colors"
          style={{ background: '#338edc' }}
        >
          {formOffen ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
          {formOffen ? t('common.cancel', 'Abbrechen') : t('stempel.terminals.neu', 'Terminal registrieren')}
        </button>
      </div>

      <div className="px-5 py-4">
        <p className="text-xs mb-4" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
          {t('stempel.terminals.info', 'Ein registriertes Gerät ist ohne PIN scanbereit und zeigt die Terminal-Ansicht (hell, ohne Dark-Mode-Schalter, Button statt Slider). Alle anderen Geräte funktionieren normal weiter und zeigen die persönliche Ansicht; dort bleibt die Terminal-PIN als Notweg. Deaktivieren nimmt ein Terminal vorübergehend außer Betrieb, ohne die Registrierung zu verlieren.')}
        </p>

        {formOffen && (
          <div className="mb-4 p-4 rounded-xl" style={{ background: 'var(--bg-inset, #F8F9FC)' }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('stempel.terminals.name', 'Name, z. B. Terminal Halle 1')}
                className="px-3 py-2 text-sm rounded-lg bg-surface-elevated border border-line text-content focus:outline-none focus:border-[#338edc]"
              />
              <input
                type="text"
                value={standort}
                onChange={(e) => setStandort(e.target.value)}
                placeholder={t('stempel.terminals.standortPlaceholder', 'Standort (optional)')}
                className="px-3 py-2 text-sm rounded-lg bg-surface-elevated border border-line text-content focus:outline-none focus:border-[#338edc]"
              />
            </div>
            <button
              onClick={anlegen}
              disabled={!name.trim() || saving}
              className="mt-3 px-4 py-2 text-xs font-medium rounded-lg text-white"
              style={{ background: '#338edc', opacity: !name.trim() || saving ? 0.5 : 1 }}
            >
              {saving ? '...' : t('stempel.terminals.codeErstellen', 'Kopplungscode erstellen')}
            </button>
          </div>
        )}

        {loading ? (
          <p className="text-sm" style={{ color: 'var(--text-tertiary, #6B7280)' }}>...</p>
        ) : geraete.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
            {t('stempel.terminals.leer', 'Noch kein Terminal registriert.')}
          </p>
        ) : (
          <div className="space-y-2">
            {geraete.map(g => (
              <div
                key={g.id}
                className="flex flex-wrap items-center gap-3 justify-between py-3 px-4 rounded-xl border border-line"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setDetailId(g.id)}
                      className="text-sm font-medium text-content underline decoration-transparent hover:decoration-inherit text-left"
                    >
                      {g.name}
                    </button>
                    <span
                      className="text-[11px] font-medium px-2 py-0.5 rounded-full"
                      style={STATUS_STYLE[g.status]}
                    >
                      {STATUS_TEXT[g.status]}
                    </span>
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                    {g.standort ? `${g.standort} · ` : ''}
                    {g.status === 'gesperrt'
                      ? `deaktiviert ${datum(g.gesperrt_seit)}${g.gesperrt_von_name ? ` von ${g.gesperrt_von_name}` : ' am Terminal'}`
                      : g.status === 'aktiv'
                        ? `gekoppelt ${datum(g.gekoppelt_am)} · zuletzt aktiv ${datum(g.letzte_nutzung)}`
                        : `angelegt ${datum(g.erstellt_am)}${g.erstellt_von_name ? ` von ${g.erstellt_von_name}` : ''}`}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  {g.pairing_code && (
                    <div className="text-right">
                      <code
                        className="text-base px-3 py-1.5 rounded-lg font-mono font-bold tracking-widest"
                        style={{ background: 'var(--bg-inset, #F8F9FC)', color: 'var(--text-primary, #1A1A2E)' }}
                      >
                        {g.pairing_code}
                      </code>
                      <p className="text-[11px] mt-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                        am Tablet unter /geraet-einrichten · gültig bis {datum(g.pairing_code_ablauf)}
                      </p>
                    </div>
                  )}
                  <button
                    onClick={() => setDetailId(g.id)}
                    className="text-xs underline"
                    style={{ color: '#338edc' }}
                  >
                    {t('stempel.terminals.protokoll', 'Protokoll')}
                  </button>
                  {g.status === 'gesperrt' && (
                    <button
                      onClick={() => entsperren(g.id)}
                      className="text-xs font-medium underline"
                      style={{ color: '#166534' }}
                    >
                      {t('stempel.terminals.entsperren', 'Entsperren')}
                    </button>
                  )}
                  {g.status !== 'widerrufen' && (
                    <>
                      <button
                        onClick={() => codeNeu(g.id)}
                        className="text-xs underline"
                        style={{ color: '#338edc' }}
                      >
                        {g.gekoppelt_am
                          ? t('stempel.terminals.neuKoppeln', 'Neu koppeln')
                          : t('stempel.terminals.codeNeu', 'Neuer Code')}
                      </button>
                      <button
                        onClick={() => widerrufen(g.id)}
                        className="text-xs underline"
                        style={{ color: 'var(--text-tertiary, #6B7280)' }}
                      >
                        {t('stempel.terminals.widerrufen', 'Widerrufen')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {detailId && (
        <KioskGeraetDetail
          geraetId={detailId}
          t={t}
          showToast={showToast}
          onClose={() => setDetailId(null)}
          onChanged={laden}
        />
      )}
    </div>
  );
}
